/**
 * MFA TOTP ou e-mail + device trust (DOC 06 § 5.1.1).
 */
import { APIClient } from "./api_helper";
import {
  DELETE_ME_MFA_TRUSTED,
  GET_ME_MFA,
  POST_LOGIN_MFA,
  POST_LOGIN_MFA_RESEND,
  POST_ME_MFA_CHANGE_METHOD,
  POST_ME_MFA_CONFIRM,
  POST_ME_MFA_DISABLE,
  POST_ME_MFA_RECOVERY,
  POST_ME_MFA_SEND_CODE,
  POST_ME_MFA_SETUP,
  POST_ME_MFA_SETUP_RESEND,
} from "./url_helper";

const api = new APIClient();

export const MFA_DEVICE_TRUST_KEY = "mfa_device_trust_token";
export const MFA_RESEND_COOLDOWN_SEC = 60;

export type MfaMethod = "totp" | "email";

export type MfaTrustedDevice = {
  id: number;
  label: string | null;
  last_used_at: string | null;
  expires_at: string | null;
};

export type MfaStatus = {
  enabled: boolean;
  method: MfaMethod | null;
  confirmed_at: string | null;
  recovery_codes_remaining: number;
  trusted_devices: number;
  devices?: MfaTrustedDevice[];
};

export type MfaSetupPayload = {
  method: MfaMethod;
  secret?: string;
  otpauth_url?: string;
  email_hint?: string;
  switching?: boolean;
};

function unwrapError(envelope: any, fallback: string): never {
  throw new Error(envelope?.error?.message || fallback);
}

export function getStoredDeviceTrustToken(): string | null {
  try {
    return localStorage.getItem(MFA_DEVICE_TRUST_KEY);
  } catch {
    return null;
  }
}

export function storeDeviceTrustToken(token: string | null | undefined): void {
  try {
    if (token) {
      localStorage.setItem(MFA_DEVICE_TRUST_KEY, token);
    } else {
      localStorage.removeItem(MFA_DEVICE_TRUST_KEY);
    }
  } catch {
    // private mode
  }
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const envelope: any = await api.get(GET_ME_MFA);
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_status_failed");
  }
  return envelope.data as MfaStatus;
}

export async function setupMfa(method: MfaMethod): Promise<MfaSetupPayload> {
  const envelope: any = await api.create(POST_ME_MFA_SETUP, { method });
  if (!envelope?.success || !envelope?.data?.method) {
    unwrapError(envelope, "mfa_setup_failed");
  }
  return envelope.data as MfaSetupPayload;
}

export async function changeMfaMethod(
  code: string,
  method: MfaMethod
): Promise<MfaSetupPayload> {
  const envelope: any = await api.create(POST_ME_MFA_CHANGE_METHOD, {
    code,
    method,
  });
  if (!envelope?.success || !envelope?.data?.method) {
    unwrapError(envelope, "mfa_change_failed");
  }
  return envelope.data as MfaSetupPayload;
}

export async function resendMfaSetupCode(): Promise<MfaSetupPayload> {
  const envelope: any = await api.create(POST_ME_MFA_SETUP_RESEND, {});
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_resend_failed");
  }
  return envelope.data as MfaSetupPayload;
}

export async function sendMfaActionCode(): Promise<MfaSetupPayload> {
  const envelope: any = await api.create(POST_ME_MFA_SEND_CODE, {});
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_send_code_failed");
  }
  return envelope.data as MfaSetupPayload;
}

export async function confirmMfa(code: string): Promise<{
  recovery_codes?: string[];
  confirmed_at: string;
  method: MfaMethod;
  switched?: boolean;
  message?: string;
}> {
  const envelope: any = await api.create(POST_ME_MFA_CONFIRM, { code });
  if (!envelope?.success || !envelope?.data?.method) {
    unwrapError(envelope, "mfa_confirm_failed");
  }
  return {
    recovery_codes: envelope.data.recovery_codes,
    confirmed_at: envelope.data.confirmed_at,
    method: envelope.data.method,
    switched: envelope.data.switched,
    message: envelope.message,
  };
}

export async function disableMfa(
  currentPassword: string | null,
  code: string
): Promise<{ message?: string }> {
  const body: { code: string; current_password?: string } = { code };
  if (currentPassword) {
    body.current_password = currentPassword;
  }
  const envelope: any = await api.create(POST_ME_MFA_DISABLE, body);
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_disable_failed");
  }
  storeDeviceTrustToken(null);
  return { message: envelope.message };
}

export async function revokeMfaTrustedDevices(): Promise<{
  trusted_devices: number;
  devices: MfaTrustedDevice[];
  message?: string;
}> {
  const envelope: any = await api.delete(DELETE_ME_MFA_TRUSTED);
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_revoke_trust_failed");
  }
  storeDeviceTrustToken(null);
  return {
    trusted_devices: envelope.data?.trusted_devices ?? 0,
    devices: envelope.data?.devices ?? [],
    message: envelope.message,
  };
}

export async function revokeMfaTrustedDevice(deviceId: number): Promise<{
  trusted_devices: number;
  devices: MfaTrustedDevice[];
  message?: string;
}> {
  const envelope: any = await api.delete(
    `${DELETE_ME_MFA_TRUSTED}/${deviceId}`
  );
  if (!envelope?.success) {
    unwrapError(envelope, "mfa_revoke_trust_failed");
  }
  return {
    trusted_devices: envelope.data?.trusted_devices ?? 0,
    devices: envelope.data?.devices ?? [],
    message: envelope.message,
  };
}

export async function regenerateMfaRecoveryCodes(
  code: string
): Promise<{ recovery_codes: string[]; message?: string }> {
  const envelope: any = await api.create(POST_ME_MFA_RECOVERY, { code });
  if (!envelope?.success || !envelope?.data?.recovery_codes) {
    unwrapError(envelope, "mfa_recovery_failed");
  }
  return {
    recovery_codes: envelope.data.recovery_codes,
    message: envelope.message,
  };
}

export async function postLoginMfa(payload: {
  mfa_token: string;
  code: string;
  remember_device?: boolean;
}): Promise<any> {
  return api.create(POST_LOGIN_MFA, payload);
}

export async function postLoginMfaResend(mfaToken: string): Promise<any> {
  return api.create(POST_LOGIN_MFA_RESEND, { mfa_token: mfaToken });
}
